(ns ui.gallery.page
  "The Replicant gallery page under /ui/gallery. The `/#/gallery` hash
   route of the former Reagent gallery redirects here (see
   `storrito.web.index/legacy-hash-redirect-js`).

   The page composes its own frame (navbar + sidebar) instead of
   `ui.app-frame/app-frame`, since the gallery grid scrolls inside the
   page, which needs the exact viewport height."
  (:require [parts.replicant.query :as query]
            [ui.app-frame :as app-frame]
            [ui.gallery.folder-tree :as folder-tree]
            [ui.gallery.grid :as grid]
            [ui.gallery.header :as header]
            [ui.gallery.move-popover :as move-popover]
            [ui.gallery.query :as gq]
            [ui.gallery.selection-bar :as selection-bar]
            [ui.gallery.tabs :as tabs]
            [ui.navbar :as navbar]
            [ui.sidebar :as sidebar]
            [ui.user-session :as user-session]))

(defn frame
  "Only the very first load shows the placeholder: a refresh of the
   session query must not replace the whole page while it runs."
  [state content]
  (if (and (query/loading? state app-frame/user-session-query)
           (not (query/available? state app-frame/user-session-query)))
    [:div "loading..."]
    ;; `h-full` instead of `h-dvh`: browsers without the `dvh` unit
    ;; (Chrome < 108) drop the declaration, then the frame grows to the
    ;; height of its content and the grid never scrolls. `#app` already
    ;; has the viewport height (see style.css):
    [:div.flex.flex-col.h-full
     (navbar/navbar state)
     [:main.flex-1.min-h-0.overflow-hidden
      [:div.flex.h-full.relative.overflow-hidden
       (sidebar/sidebar-toggle state)
       (when-not (:ui/hide-side-menu? state)
         (sidebar/sidebar state))
       [:div.flex.flex-col.grow.min-w-0.min-h-0.bg-gray-100
        content]]]
     (when-not (:ui/hide-side-menu? state)
       (app-frame/book-call-button (user-session/org-uuid state)))]))

(defn empty-view?
  "Whether the empty card (or the empty archive hint) replaces the grid:
   only when the result belongs to the shown tab, since after a tab
   switch the value of the previous tab is shown until the refresh
   arrives, and an empty archive must not flash the empty card."
  [result mode]
  (and (= 0 (:gallery/entry-count result))
       (= mode (:gallery/mode result))))

(defn content
  "The grid, or what replaces it. Each variant has its own key, so
   Replicant mounts the grid fresh (see `grid/grid`) when it takes the
   place of a variant."
  [state result mode]
  (cond
    (nil? result)
    [:div.flex.justify-center.items-center.h-full.text-gray-500
     {:replicant/key ::placeholder}
     (if (gq/error? state)
       "The gallery could not be loaded. Please reload the page."
       "Loading...")]

    (and (empty-view? result mode)
         (= mode :active))
    (header/empty-card)

    (empty-view? result mode)
    (header/archive-empty-hint)

    :else
    (grid/grid state result)))

(defn render
  [state]
  (let [gallery (:gallery state)
        result (gq/result state)
        mode (or (:mode gallery) :active)
        folder-tree (:folder-tree result)]
    (frame
      state
      [:div.flex.flex-col.grow.min-h-0.relative
       {:on {:dragenter [[:gallery/file-drag-enter]]}}
       (when (:file-drag? gallery)
         (header/drop-overlay))
       [:div.flex.flex-col.gap-y-2.sm:gap-y-4.p-4.shrink-0
        (header/header-section gallery)
        (tabs/tab-bar mode)]
       ;; The folder tree is also shown while the organization has no
       ;; folders yet (`folder-tree` is nil then), like in the Reagent
       ;; gallery: otherwise the users never learn that there are folders.
       [:div.flex.grow.min-h-0.overflow-hidden.pb-4.pl-4.pr-4
        (when (= mode :active)
          [:span.hidden.md:block.h-full
           (folder-tree/panel gallery folder-tree)])
        [:div.flex.flex-col.grow.min-w-0.min-h-0
         (when (= mode :active)
           [:div.justify-center.w-full.block.md:hidden
            (move-popover/change-folder-popover gallery folder-tree)])
         (folder-tree/current-folder-drop-zone-wrapper
           gallery
           (:current-folder folder-tree)
           (content state result mode))]]
       (selection-bar/selection-bar gallery
                                    folder-tree
                                    (:gallery/entry-count result))
       (folder-tree/context-menu gallery)])))

(def page
  {:page-id :pages/gallery
   :route [["ui" "gallery"]]
   ;; `:gallery/init` loads the user session once; the on-load runs
   ;; again for every folder navigation, which must not re-query it.
   :on-load (fn [location]
              [[:ui/hide-sidebar-on-mobile]
               [:gallery/watch-uploads]
               [:gallery/init location]])
   :render #'render})
